import { IOccupancyPerTime, IRestaurantShift } from '@localina/core';

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_SLOT_MINUTES = 15;
const TICK_STEPS = [60, 120, 180, 240, 360, 720];
const MAX_TICKS = 7;
// Room one "HH:mm" label needs, gap included; ticks closer than this would collide.
const TICK_LABEL_SPACING = 56;

// Minutes since midnight of the stats date; values past 1440 belong to the night after.
export interface ITimeRange {
    readonly from: number;
    readonly to: number;
}

export interface ITimeTick {
    readonly minutes: number;
    readonly align: 'start' | 'center' | 'end';
}

const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const formatMinutes = (minutes: number): string => {
    const value = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
};

export const getShiftRange = (shift: IRestaurantShift): ITimeRange => {
    const from = toMinutes(shift.from);
    const to = toMinutes(shift.to);
    return { from, to: to <= from ? to + MINUTES_PER_DAY : to };
};

// Slot times as minutes, kept increasing so a list that runs past midnight stays in order.
// `from` places a list that starts after midnight behind a range that starts before it.
export const getSlotMinutes = (list: IOccupancyPerTime[], from = 0): number[] => {
    const result: number[] = [];
    list.forEach((it) => {
        let minutes = toMinutes(it.time);
        const floor = result.length > 0 ? result[result.length - 1] : from - MINUTES_PER_DAY / 2;
        while (minutes < floor) {
            minutes += MINUTES_PER_DAY;
        }
        result.push(minutes);
    });
    return result;
};

export const getSlotDuration = (slots: number[]): number => {
    let duration = Infinity;
    for (let i = 1; i < slots.length; i++) {
        const diff = slots[i] - slots[i - 1];
        if (diff > 0 && diff < duration) {
            duration = diff;
        }
    }
    return Number.isFinite(duration) ? duration : DEFAULT_SLOT_MINUTES;
};

export const getDayRange = (list: IOccupancyPerTime[], shifts: IRestaurantShift[]): ITimeRange | undefined => {
    const shiftRanges = shifts.map(getShiftRange);
    const slots = getSlotMinutes(list);
    const starts = [...shiftRanges.map((it) => it.from), ...slots.slice(0, 1)];
    const ends = [...shiftRanges.map((it) => it.to), ...slots.slice(-1).map((it) => it + getSlotDuration(slots))];
    if (starts.length === 0) {
        return undefined;
    }
    const from = Math.min(...starts);
    const to = Math.max(...ends);
    return to > from ? { from, to } : undefined;
};

export const toPercent = (minutes: number, range: ITimeRange): number =>
    ((minutes - range.from) / (range.to - range.from)) * 100;

// The range's edges are always labelled; round hours in between, dropping any that crowd an edge.
// With the axis width known, labels thin out as it narrows (never more than MAX_TICKS), and no
// tick sits closer to an edge than half a step or one label's room. Before the first measurement,
// the span alone decides.
export const getTicks = (range: ITimeRange, width?: number): ITimeTick[] => {
    const span = range.to - range.from;
    const maxTicks = width ? Math.max(1, Math.min(MAX_TICKS, Math.floor(width / TICK_LABEL_SPACING))) : MAX_TICKS;
    const step = TICK_STEPS.find((it) => span / it <= maxTicks) ?? TICK_STEPS[TICK_STEPS.length - 1];
    const minGap = Math.max(step / 2, width ? (span * TICK_LABEL_SPACING) / width : 0);
    const ticks: ITimeTick[] = [{ minutes: range.from, align: 'start' }];
    for (let minutes = Math.ceil(range.from / step) * step; minutes < range.to; minutes += step) {
        if (minutes - range.from >= minGap && range.to - minutes >= minGap) {
            ticks.push({ minutes, align: 'center' });
        }
    }
    ticks.push({ minutes: range.to, align: 'end' });
    return ticks;
};

// Greedy interval partitioning: shifts that overlap in time go to separate lanes.
export const getLanes = <T>(items: T[], getRange: (item: T) => ITimeRange): T[][] => {
    const lanes: { end: number; items: T[] }[] = [];
    [...items]
        .sort((a, b) => getRange(a).from - getRange(b).from)
        .forEach((item) => {
            const range = getRange(item);
            const lane = lanes.find((it) => it.end <= range.from);
            if (lane) {
                lane.items.push(item);
                lane.end = range.to;
            } else {
                lanes.push({ end: range.to, items: [item] });
            }
        });
    return lanes.map((it) => it.items);
};
